/**
 * Agentic Payment Offers - Shared Styles
 *
 * Re-uses the same color palette and component styles as
 * AgenticPaymentTransactionHistory for visual consistency.
 */

// ============================================================================
// Color Palette
// ============================================================================

export const colors = {
  primary: "#2e3b61",
  primaryLight: "#3d4d7a",
  accent: "#e58734",
  accentDark: "#d77a2a",
  success: "#28a745",
  successDark: "#218838",
  danger: "#dc3545",
  dangerDark: "#c82333",
  warning: "#ffc107",
  warningText: "#856404",
  warningBg: "#fff3cd",
  info: "#17a2b8",
  infoDark: "#138496",
  white: "#fff",
  black: "#000",
  gray100: "#f8f9fa",
  gray200: "#f5f5f5",
  gray300: "#e0e0e0",
  gray400: "#ccc",
  gray500: "#999",
  gray600: "#666",
  gray700: "#333",
  bgLight: "#fafafa",
  bgMedium: "#f0f0f0",
  border: "#e0e0e0",
  borderLight: "#ddd",
};

// ============================================================================
// Gradients
// ============================================================================

export const gradients = {
  primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  accent: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
  success: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
  danger: `linear-gradient(135deg, ${colors.danger} 0%, ${colors.dangerDark} 100%)`,
  info: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoDark} 100%)`,
  warning: `linear-gradient(135deg, #ffc107 0%, #e0a800 100%)`,
  gray: `linear-gradient(135deg, #6c757d 0%, #5a6268 100%)`,
  purple: `linear-gradient(135deg, #6c3483 0%, #5b2c6f 100%)`,
};

// ============================================================================
// Page Header Styles
// ============================================================================

export const pageHeaderStyles = {
  container: {
    background: gradients.primary,
    padding: "28px 32px",
    borderRadius: "8px 8px 0 0",
    marginBottom: "0",
  },
  title: {
    margin: "0 0 12px 0",
    fontSize: "28px",
    fontWeight: 700,
    color: colors.white,
    letterSpacing: "0.3px",
  },
  subtitle: {
    margin: 0,
    fontSize: "14px",
    color: "rgba(255,255,255,0.75)",
  },
};

// ============================================================================
// Common Styles
// ============================================================================

export const commonStyles = {
  panel: {
    background: colors.white,
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    background: gradients.primary,
    borderRadius: "6px 6px 0 0",
    marginBottom: "20px",
    borderBottom: `3px solid ${colors.accent}`,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: `1px solid ${colors.gray400}`,
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  buttonPrimary: {
    background: gradients.primary,
    color: colors.white,
  },
  filterBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    background: colors.gray100,
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  badge: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 600,
    color: colors.primary,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
};

// ============================================================================
// Pagination Styles
// ============================================================================

export const paginationStyles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    background: colors.gray200,
    borderTop: `1px solid ${colors.border}`,
    flexWrap: "wrap",
    gap: "12px",
  },
  info: {
    fontSize: "13px",
    color: colors.gray600,
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  navButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 14px",
    height: "36px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "4px",
    userSelect: "none",
    cursor: "pointer",
  },
  pageButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "36px",
    height: "36px",
    padding: "0 8px",
    fontSize: "14px",
    borderRadius: "4px",
    userSelect: "none",
    cursor: "pointer",
    border: "none",
  },
};

// ============================================================================
// Modal Styles
// ============================================================================

export const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  content: {
    background: colors.white,
    borderRadius: "8px",
    maxWidth: "780px",
    width: "90%",
    maxHeight: "85vh",
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    background: gradients.primary,
    borderBottom: `3px solid ${colors.accent}`,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  headerTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: colors.white,
  },
  closeButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    background: "rgba(255,255,255,0.15)",
    color: colors.white,
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: "4px",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "1",
    whiteSpace: "nowrap",
  },
  body: {
    padding: "20px",
  },
  detailRow: {
    display: "flex",
    padding: "12px 0",
    borderBottom: `1px solid ${colors.border}`,
  },
  detailLabel: {
    width: "180px",
    fontWeight: 600,
    color: colors.primary,
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    flexShrink: 0,
  },
  detailValue: {
    flex: 1,
    fontSize: "14px",
    color: colors.gray700,
    wordBreak: "break-all",
  },
  sectionTitle: {
    margin: "20px 0 12px",
    fontSize: "14px",
    fontWeight: 700,
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `2px solid ${colors.accent}`,
    paddingBottom: "6px",
  },
};

// ============================================================================
// Button Styles
// ============================================================================

export const buttonStyles = {
  viewDetails: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 14px",
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
    color: colors.white,
    border: "none",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(229,135,52,0.25)",
    textShadow: "none",
    letterSpacing: "0.2px",
    lineHeight: "1.4",
    WebkitAppearance: "none",
    appearance: "none",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  viewDetailsHover: {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 14px rgba(229,135,52,0.4)",
  },
  viewDetailsDefault: {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(229,135,52,0.25)",
  },
  ghost: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 20px",
    background: "rgba(255,255,255,0.12)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    color: colors.white,
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    textShadow: "none",
    letterSpacing: "0.2px",
    lineHeight: "1.4",
    WebkitAppearance: "none",
    appearance: "none",
    boxShadow: "none",
    transition:
      "transform 0.2s ease, background 0.2s ease, border-color 0.2s ease",
  },
  ghostHover: {
    background: "rgba(255,255,255,0.22)",
    borderColor: "rgba(255,255,255,0.45)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
  ghostDefault: {
    background: "rgba(255,255,255,0.12)",
    borderColor: "rgba(255,255,255,0.25)",
    transform: "translateY(0)",
    boxShadow: "none",
  },
  // Admin action buttons
  adminDelist: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "10px 20px",
    background: `linear-gradient(135deg, ${colors.warning} 0%, #e0a800 100%)`,
    color: colors.gray700,
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "120px",
  },
  adminRelist: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "10px 20px",
    background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
    color: colors.white,
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "120px",
  },
  adminDelete: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "10px 20px",
    background: `linear-gradient(135deg, ${colors.danger} 0%, ${colors.dangerDark} 100%)`,
    color: colors.white,
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "120px",
  },
  adminDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  adminCancel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    background: colors.gray300,
    color: colors.gray700,
    border: `1px solid ${colors.gray400}`,
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

// ============================================================================
// Admin Action Panel Styles
// ============================================================================

export const adminActionStyles = {
  container: {
    margin: "24px 0 0",
    padding: "20px",
    background: colors.gray100,
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
  },
  title: {
    margin: "0 0 16px",
    fontSize: "14px",
    fontWeight: 700,
    color: colors.danger,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  confirmOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1100,
  },
  confirmBox: {
    background: colors.white,
    borderRadius: "8px",
    padding: "28px",
    maxWidth: "440px",
    width: "90%",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
  },
  confirmTitle: {
    margin: "0 0 12px",
    fontSize: "18px",
    fontWeight: 700,
    color: colors.gray700,
  },
  confirmText: {
    margin: "0 0 16px",
    fontSize: "14px",
    color: colors.gray600,
    lineHeight: 1.5,
  },
  reasonLabel: {
    display: "block",
    marginBottom: "6px",
    fontWeight: 600,
    fontSize: "12px",
    color: colors.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  reasonInput: {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${colors.gray400}`,
    borderRadius: "4px",
    fontSize: "14px",
    resize: "vertical",
    minHeight: "60px",
    boxSizing: "border-box",
    marginBottom: "20px",
  },
  confirmActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  alertSuccess: {
    padding: "12px 16px",
    borderRadius: "6px",
    background: "#d4edda",
    border: "1px solid #c3e6cb",
    color: "#155724",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  alertError: {
    padding: "12px 16px",
    borderRadius: "6px",
    background: "#f8d7da",
    border: "1px solid #f5c6cb",
    color: "#721c24",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "16px",
  },
};
